import {
  wrapCall,
  arrayToQuery,
  embedsToQuery,
  callGet,
  callPost,
  callPut,
  callDelete
} from './helpers'

export const DEFAULT_SESSION_EMBEDS = ['users', 'latest_activity', 'unread']

//* ~~~~~~ SESSION ROUTES ~~~~~~

// Fetches a list of session ids
export const fetchSessionList = async (query: string = '') => {
  return (await wrapCall({
    method: callGet,
    uri: `/sessions/list?${arrayToQuery('sort', [
      encodeURIComponent('-activity')
    ])}${query}`,
    noData: true
  })) as WithIndexPagination<string[]>
}

// Gets sessions through
export const fetchSessions = async (query: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/sessions${query}`,
    noData: true
  })) as WithIndexPagination<APISession[]>
}

// Gets session through bulk endpoint
export const fetchBulkSessions = async (ids: string[]) => {
  return (await wrapCall({
    method: callPost,
    uri: `/sessions/bulk`,
    data: { ids, embed: DEFAULT_SESSION_EMBEDS }
  })) as APISession[]
}

// Gets unread counts for a list of sessions
export const fetchUnreads = async (ids: string[]) => {
  return (await wrapCall({
    method: callPost,
    uri: '/sessions/unread',
    data: { ids }
  })) as APIUnread[]
}

// Create a session
export const createSession = async (data: SessionUpdate) => {
  return (await wrapCall({
    method: callPost,
    uri: '/sessions',
    data
  })) as APISession
}

// Gets information for a session
export const getSession = async (id: string) => {
  return (await wrapCall({
    method: callGet,
    uri: `/sessions/${id}${embedsToQuery(DEFAULT_SESSION_EMBEDS)}`
  })) as APISession
}

// Ends the session denoted by id
export const endSession = async (id: string, session: APISession) => {
  const data = {
    name: session.name ?? null,
    topic: session.topic ?? null,
    state: 'ENDED',
    profile: session.profile,
    owner_id: session.owner_id ?? null,
    assigned_to: session.assigned_to ?? null,
    tags: session.tags,
    userIds: session.users,
    // closed_at: session.closed_at ?? null,
    // TODO: Remove this line later
    closedAt: session.closed_at ?? null,
    inbox_id: session.inbox_id
  }
  return (await wrapCall({
    method: callPut,
    uri: `/sessions/${id}`,
    data
  })) as APISession
}

// Closes the session denoted by id
export const closeSession = async (id: string, session: APISession) => {
  const data = {
    name: session.name ?? null,
    topic: session.topic ?? null,
    state: 'CLOSED',
    profile: session.profile,
    owner_id: session.owner_id ?? null,
    assigned_to: session.assigned_to ?? null,
    tags: session.tags,
    userIds: session.users,
    // closed_at: session.closed_at ?? null,
    // TODO: Remove this line later
    closedAt: session.closed_at ?? null,
    inbox_id: session.inbox_id
  }
  return (await wrapCall({
    method: callPut,
    uri: `/sessions/${id}`,
    data
  })) as APISession
}

// Updates a session
export const updateSession = async (id: string, data: SessionUpdate) => {
  return (await wrapCall({
    method: callPut,
    uri: `/sessions/${id}`,
    data
  })) as APISession
}

// Verifies a session
export const verifySession = async (id: string) => {
  return await wrapCall({ method: callPost, uri: `/sessions/${id}/verified` })
}

//* ~~~~~~ USERS RELATED ~~~~~~

// Adding user to session
export const addParticipant = async (sessionId: string, userId: string) => {
  return await wrapCall({
    method: callPost,
    uri: `/sessions/${sessionId}/users/${userId}`,
    rawError: true
  })
}

// Removing user from session
export const removeParticipant = async (sessionId: string, userId: string) => {
  return await wrapCall({
    method: callDelete,
    uri: `/sessions/${sessionId}/users/${userId}`,
    rawError: true
  })
}
